import { loadImage } from "../../../../Publish/BulkUpload/Script/bulkUpload"

export const autoEnhanceImage = (uri) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!uri) throw "Please upload file to continue."

      let img = await loadImage(uri)

      const originalCanvas = document.createElement("canvas")
      const originalCtx = originalCanvas.getContext("2d")

      originalCanvas.width = img.width
      originalCanvas.height = img.height
      originalCtx.clearRect(0, 0, img.width, img.height)
      originalCtx.drawImage(img, 0, 0)

      const imageData = originalCtx.getImageData(0, 0, originalCanvas.width, originalCanvas.height)
      const data = imageData.data

      let minX = originalCanvas.width
      let minY = originalCanvas.height
      let maxX = 0
      let maxY = 0
      let foundSignature = false
      let totalBrightness = 0
      let pixelCount = 0
      let transparentPixels = 0

      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] != 0) {
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3
          totalBrightness += brightness
        } else {
          totalBrightness += 255
        }
        if (data[i + 3] == 0) {
          transparentPixels++
        }
        pixelCount++
      }

      const meanBrightness = totalBrightness / pixelCount
      let variance = 0
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] != 0) {
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3
          variance += Math.pow(brightness - meanBrightness, 2)
        } else {
          variance += Math.pow(255 - meanBrightness, 2)
        }
      }
      const standardDeviation = Math.sqrt(variance / pixelCount)
      if (standardDeviation < 1) throw "No signature detected. Try using a clearer image."
      // const isLightBackground = meanBrightness > 128

      let isLightBackground
      if (standardDeviation < 2) {
        // Almost uniform image — likely background only
        isLightBackground = meanBrightness > 80 // Lower threshold for safety
      } else {
        // Image has variation — apply standard logic
        isLightBackground = meanBrightness > 127
      }

      let thresholdMultiplier = 3.0
      while (meanBrightness - standardDeviation * thresholdMultiplier < 40 && thresholdMultiplier > 0.5) {
        thresholdMultiplier -= 0.1
      }

      let threshold = isLightBackground ? meanBrightness - standardDeviation * thresholdMultiplier : meanBrightness + standardDeviation * thresholdMultiplier

      if (isLightBackground && threshold < standardDeviation) {
        threshold = meanBrightness - 40
      }

      // Removing bacground logic
      const transparencyRatio = transparentPixels / (data.length / 4)
      // console.log(transparentPixels, data.length / 4, transparencyRatio, "transparrent")

      if (transparencyRatio == 0) {
        // Removing bacground logic
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          let brightness = 0.299 * r + 0.587 * g + 0.114 * b
          if (data[i + 3] == 0) {
            continue
          }
          let alpha = 255
          if (isLightBackground) {
            if (brightness > threshold) alpha = 0
          } else {
            if (brightness < threshold) alpha = 0
          }
          data[i + 3] = alpha
        }
        originalCtx.putImageData(imageData, 0, 0)
      }

      // Finding co-ordinates for signature bounding box
      for (let y = 0; y < originalCanvas.height; y++) {
        for (let x = 0; x < originalCanvas.width; x++) {
          const idx = (y * originalCanvas.width + x) * 4
          // console.log(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])
          if (data[idx + 3] !== 0) {
            // alpha is not transparent
            foundSignature = true
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
          }
        }
      }

      if (!foundSignature) throw "No signature detected. Try using a clearer image."

      const padding = 5
      const paddedMinX = Math.max(minX - padding, 0)
      const paddedMinY = Math.max(minY - padding, 0)
      const paddedMaxX = Math.min(maxX + padding, originalCanvas.width)
      const paddedMaxY = Math.min(maxY + padding, originalCanvas.height)

      const width = paddedMaxX - paddedMinX
      const height = paddedMaxY - paddedMinY

      const croppedImageData = originalCtx.getImageData(paddedMinX, paddedMinY, width, height)
      const croppedCanvas = document.createElement("canvas")
      const croppedCtx = croppedCanvas.getContext("2d")

      croppedCanvas.width = width
      croppedCanvas.height = height
      croppedCtx.clearRect(0, 0, width, height)
      croppedCtx.putImageData(croppedImageData, 0, 0)

      // console.log({ paddedMinX, paddedMinY, width, height, threshold })

      let blobObject = await canvasToBlob(croppedCanvas)

      let finalImage = URL.createObjectURL(blobObject)

      resolve({
        paddedMinX,
        paddedMinY,
        width,
        height,
        threshold: Math.floor(threshold),
        finalImage,
      })
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Converts a canvas element to a Blob object.
 *
 * @param {HTMLCanvasElement} canvas - The canvas element to be converted.
 * @returns {Promise<Blob>} A Promise that resolves with the generated Blob object.
 */
const canvasToBlob = (canvas) => {
  /**
   * @typedef {Blob} Blob - Represents a binary large object (Blob) that can store binary data.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Blob Blob}
   */

  return new Promise((resolve) => {
    /**
     * The `toBlob` method of the canvas element is used to convert it to a Blob.
     * @param {Blob} blob - The generated Blob object.
     */
    canvas.toBlob((blob) => {
      // Once the Blob is created, the Promise is resolved with the Blob object.
      resolve(blob)
    }, "image/png")
  })
}
